import React, {useEffect, useState} from "react";
import Topbar from "../top-bar/top-bar";
import ActionCard from "../action-card/action-card";
import {instanceMob} from "../../utils/utils";
import DataTableCard from "../data-table-card/data-table-card";
import {useNavigate} from "react-router-dom";
import Dropdown from "../dropdown/dropdown";
import getActionCards from "./config/action-cards.config";
import ReceiptModal from "./modals/receipt-modal";
import useLocationStore from "./config/use-location-store";
import LocationModal from "./modals/location-modal";
import SaleModal from "./modals/sale-modal";
import PurchaseModal from "./modals/purchase-modal";
import "./accounting.css";
import PaymentReceiptModal from "./modals/payment-receipt-modal";
import CashBoxModal from "./modals/cash-box-modal";
import InventoryMovementModal from "./modals/inventory-movement-modal";
import ProductRegistrationModal from "./modals/product-registration-modal";
import PartnerLoanModal from "./modals/partner-loan-modal";
import useCashboxStore, {STATUS_OPEN} from "./config/use-cashbox-store";
import CashboxStatusModal from "./modals/cashbox-status-modal";

const Accounting = () => {
    const navigate = useNavigate();
    const [locations, setLocations] = useState([]);
    const {selectedLocation, setSelectedLocation} = useLocationStore();
    const {cashboxStatus, fetchCashbox} = useCashboxStore();
    const isCashboxOpen = cashboxStatus === STATUS_OPEN;
    const [openModal, setOpenModal] = useState(
        selectedLocation ? null : "location"
    );
    const [loadingTables, setLoadingTables] = useState(false);
    const [alertsData, setAlertsData] = useState({
        title: "Alertas de inventario",
        count: 0,
        columns: ["Producto", "Stock"],
        rows: [],
    });
    const [debtsData, setDebtsData] = useState({
        title: "Alertas Cuentas de Cobro",
        count: 0,
        columns: ["Nombre", "Deuda"],
        rows: [],
    });
    const [bannerLoaded, setBannerLoaded] = useState(false);

    const handleOpen = (modalName) => setOpenModal(modalName);

    const handleClose = async () => {
        setOpenModal(null);

        if (selectedLocation?.id) {
            await fetchCashbox();
            getAlerts(selectedLocation.id);

            const {cashboxStatus} = useCashboxStore.getState();
            if (cashboxStatus !== STATUS_OPEN) {
                setOpenModal("cashbox-status")
            }
        }
    };

    const handleCashboxStatusClose = (shouldOpenCashbox = false) => {
        if (shouldOpenCashbox) {
            setOpenModal("cashbox");
        } else {
            setOpenModal("");
        }
    };

    const handleDownloadPartnerReport = async (locationId, type = "partner") => {
        try {
            if (!locationId) {
                return;
            }

            const response = await instanceMob.get(
                `admin/accounting/receivable/excel?location_id=${locationId}&type=${type}`,
                {
                    responseType: "blob",
                }
            );

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `report_${type}_${locationId}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al descargar el informe:", error);
        }
    };

    const actionCards = React.useMemo(() =>
            getActionCards(handleOpen, navigate, {
                downloadPartnerReport: () => handleDownloadPartnerReport(selectedLocation?.id, "partner"),
            }),
        [isCashboxOpen]);

    const getLocations = () => {
        return instanceMob
            .get(`admin/location/list`)
            .then((res) => res.data)
            .then((data) => setLocations(data))
            .catch((err) => console.error(err));
    };

    const handleLocationSelect = (selectedLoc) => {
        setSelectedLocation(selectedLoc);
    };

    const getAlerts = (locationId) => {
        if (!locationId) return;
        setLoadingTables(true);

        Promise.all([
            instanceMob.get(`admin/accounting/stock/list?location_id=${locationId}`),
            instanceMob.get(`admin/accounting/debts/list?location_id=${locationId}`)
        ])
            .then(([stockRes, debtsRes]) => {
                const stockData = stockRes.data || [];
                const stockRows = stockData.map((item) => [
                    {value: item.name},
                    {value: item.quantity}
                ]);
                const stockAlerts = stockData.filter(
                    (item) => item.quantity <= item.min_stock
                );
                setAlertsData({
                    title: "Alertas de inventario",
                    count: stockAlerts.length,
                    columns: ["Producto", "Stock"],
                    rows: stockRows,
                });

                const debtsDataRes = debtsRes.data || [];
                const debtRows = debtsDataRes.map((item) => [
                    {value: item.name},
                    {value: "$" + item.debt_balance}
                ]);
                setDebtsData({
                    title: "Alertas Cuentas de Cobro",
                    count: debtRows.length,
                    columns: ["Nombre", "Deuda"],
                    rows: debtRows,
                });
            })
            .catch((err) => console.error("Error cargando alertas:", err))
            .finally(() => setLoadingTables(false));
    };

    useEffect(() => {
        getLocations();
    }, []);

    useEffect(() => {
        if (selectedLocation?.id) {
            getAlerts(selectedLocation.id);
        }
    }, [selectedLocation]);

    useEffect(() => {
        if (selectedLocation?.id) {
            fetchCashbox();

            const {cashboxStatus} = useCashboxStore.getState();
            if (cashboxStatus !== STATUS_OPEN) {
                setOpenModal("cashbox-status")
            }
        }
    }, [selectedLocation]);

    return (<>
        <div className="accounting-main w-100">
            <Topbar/>
            <div className="accounting w-100 p-3 px-5 gap-4 gap-xxl-5 d-flex flex-column">
                <div className="accounting-header row g-3 align-items-stretch justify-content-center align-self-xxl-center">
                    <div className="col-4 col-md-4 d-flex">
                        <div className="accounting-banner w-100">
                            <label>¡Tu crecimiento comienza hoy!</label>
                            <label>
                                Panel de Negocio <strong>incluido por 3 meses</strong> con{" "}
                                <strong>Elevva Plus</strong>.
                            </label>
                        </div>
                    </div>

                    <div
                        className="col-8 col-md-8 d-flex"
                    >
                        <div
                            className="accounting-panel-link d-flex flex-row justify-content-between align-items-center p-4 cursor-pointer"
                            onClick={() => navigate("/accounting/dashboard")}
                        >
                        <span className="accounting-panel-link-title">
                          {new Date().toLocaleDateString("es-ES", {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                          })}
                        </span>
                            <span className="accounting-panel-link-title d-flex flex-row align-items-center gap-2">
                            Ingresa al panel de control interno
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                 fill="none">
                                <path
                                    d="M12 4C16.4183 4 20 7.58172 20 12C20 16.4183 16.4183 20 12 20C8.82996 20 6.09073 18.1561 4.7959 15.4824L11.4219 13.708C11.6085 13.658 11.8954 13.5841 12.1367 13.4834C12.4184 13.3658 12.8385 13.1343 13.1201 12.6465C13.4015 12.1587 13.3925 11.6796 13.3535 11.377C13.32 11.1176 13.2404 10.8321 13.1904 10.6455L11.415 4.02344C11.6083 4.00947 11.8032 4 12 4ZM10.1621 10.9385L4.02344 12.583C4.00956 12.3904 4 12.1961 4 12C4 8.83059 5.84279 6.09104 8.51562 4.7959L10.1621 10.9385Z"
                                    fill="#394F65"/>
                                <path
                                    d="M9.92945 4.27259C9.67849 3.33602 9.55302 2.86773 9.12083 2.67286C8.68865 2.47799 8.30723 2.66782 7.54439 3.04748C6.97028 3.33321 6.42361 3.67419 5.91239 4.06647C4.87054 4.8659 3.99636 5.86272 3.33975 7C2.68314 8.13728 2.25696 9.39275 2.08555 10.6947C2.00144 11.3336 1.97948 11.9775 2.01909 12.6176C2.07171 13.4681 2.09803 13.8933 2.48288 14.1701C2.86773 14.447 3.33602 14.3215 4.27259 14.0706L10.0681 12.5176C10.9788 12.2736 11.4342 12.1516 11.6413 11.7929C11.8484 11.4342 11.7264 10.9788 11.4824 10.0681L9.92945 4.27259Z"
                                    fill="#394F65"/>
                            </svg>
                        </span>
                        </div>
                    </div>
                </div>

                <div className="d-flex flex-row justify-content-sm-between justify-content-xxl-center gap-4">
                    <div className="d-flex flex-column gap-4 gap-xxl-5">
                        <div className="accounting-title">
                            Panel Principal de Gestión
                        </div>
                        <div className="accounting-subtitle">
                            Administra las operaciones clave de las líneas de negocio de tu gimnasio en un solo lugar.
                            Controla el inventario, las cuentas por cobrar, los ingresos, los egresos y los movimientos
                            de caja. Toda la información operativa, financiera y de clientes está disponible en tiempo
                            real para facilitar la toma de decisiones.
                        </div>

                        <div className="d-flex">
                            <Dropdown
                                label="Locación"
                                items={locations.map(loc => ({id: loc.id, name: loc.name}))}
                                onSelect={handleLocationSelect}
                                placeholder="Selecciona tu locación"
                                initialValue={selectedLocation?.name}
                            />
                        </div>
                    </div>
                    <div className="action-cards-container d-flex flex-row flex-wrap gap-4 gap-xxl-5">
                        {actionCards.map((card, index) => (
                            <ActionCard
                                key={index}
                                {...card}
                            />
                        ))}
                    </div>
                </div>

                <div
                    className="d-flex flex-row justify-content-sm-between justify-content-xxl-center align-items-center gap-2 gap-xxl-5 mt-4">
                    <DataTableCard data={alertsData} isLoading={loadingTables}/>
                    <DataTableCard data={debtsData} isLoading={loadingTables}/>
                </div>
            </div>
        </div>

        {openModal === "cashbox-status" && (
            <CashboxStatusModal show={true} onClose={handleCashboxStatusClose}/>
        )}

        {openModal === "location" && (
            <LocationModal show={true} onClose={handleClose}/>
        )}

        {openModal === "receipt" && (
            <ReceiptModal show={true} onClose={handleClose}/>
        )}

        {openModal === "sale" && (
            <SaleModal show={true} onClose={handleClose}/>
        )}

        {openModal === "purchase" && (
            <PurchaseModal show={true} onClose={handleClose}/>
        )}

        {openModal === "payment" && (
            <PaymentReceiptModal show={true} onClose={handleClose}/>
        )}

        {openModal === "cashbox" && (
            <CashBoxModal show={true} onClose={handleClose}/>
        )}

        {openModal === "cashbox" && (
            <CashBoxModal show={true} onClose={handleClose}/>
        )}

        {openModal === "inventory" && (
            <InventoryMovementModal show={true} onClose={handleClose}/>
        )}

        {openModal === "product" && (
            <ProductRegistrationModal show={true} onClose={handleClose}/>
        )}

        {openModal === "partner" && (
            <PartnerLoanModal show={true} onClose={handleClose}/>
        )}
    </>);
}
export default Accounting;

